import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/ui/components/primitives/dialog";
import { Badge } from "@/ui/components/primitives/badge";
import { Button } from "@/ui/components/primitives/button";
import { StatusBadge } from "@/modules/shared/components/status-badge";
import { TransactionTypeBadge } from "@/modules/shared/components/transaction-type-badge";
import type { UITxn } from "@/modules/shared/types";
import { formatDateIST } from "@/utils/date-formatter";
import { useState } from "react";
import { Check, Copy, CheckCircle2, XCircle } from "lucide-react";
import { showSuccessToast, showErrorToast } from "@/services/toast-helper";

type Props = { open: boolean; onOpenChange: (v: boolean) => void; txn: UITxn | null };

export function TransactionDetailModal({ open, onOpenChange, txn }: Props) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const r = txn?.raw;
  // Updated customer data mapping for new API structure
  const customerName = r?.customer?.name;
  const customerEmail = r?.customer?.email;
  const customerPhone = r?.customer?.phone;
  const method = txn?.method || txn?.paymentMethod || r?.paymentMethod || r?.providerResponse?.paymentMethod;
  const checkoutUrl = r?.providerResponse?.checkoutUrl;
  const reference = r?.reference || r?.providerResponse?.metadata?.reference;
  const createdAt = r?.createdAt || r?.providerResponse?.createdAt;
  const updatedAt = r?.updatedAt;
  const completedAt = r?.completedAt;
  const currency = r?.currency || "INR";
  const note = r?.note;
  const webhookDelivered = r?.webhookDelivered;

  // Use feeAmount and netAmount from API response, with fallbacks
  const feeAmount = txn?.fee || txn?.feeAmount || r?.feeAmount || 0;
  const netAmount = txn?.net || txn?.netAmount || r?.netAmount || txn?.amount || 0;

  // Use pre-formatted IST dates if available
  const formattedCreatedAt = r?.formattedCreatedAt || formatDateIST(createdAt);
  const formattedCompletedAt = r?.formattedCompletedAt || formatDateIST(completedAt);

  // Handle copy to clipboard with feedback
  const handleCopyReference = async () => {
    try {
      await navigator.clipboard.writeText(reference || "");
      setCopiedField("reference");
      showSuccessToast("Reference ID copied to clipboard");
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      showErrorToast("Failed to copy Reference ID");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Transaction details</span>
            <div className="flex items-center gap-2">
              {txn && <TransactionTypeBadge type={txn.type} />}
              {txn && <StatusBadge status={txn.status} />}
            </div>
          </DialogTitle>
        </DialogHeader>
        {/* Error Message for Cancelled Transactions */}
        {txn?.errorMessage && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-2">
              <span className="text-xs font-medium text-destructive">Error Message:</span>
              <p className="text-sm text-destructive flex-1">{txn.errorMessage}</p>
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Amount</p>
            <p className="font-semibold">{currency} ₹{txn?.amount.toLocaleString()}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Net</p>
            <p className="font-semibold">{currency} ₹{netAmount.toLocaleString()}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Fee</p>
            <p className="text-sm">₹{feeAmount.toLocaleString()}</p>
          </div>
          {/* Show Method only if available */}
          {method && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Method</p>
              <p className="text-sm">{method}</p>
            </div>
          )}

          {/* Show Reference only if available */}
          {reference && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Reference</p>
              <p className="text-sm break-all">{reference}</p>
            </div>
          )}
          {/* Show dates only if available */}
          {createdAt && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Created</p>
              <p className="text-sm">{formattedCreatedAt}</p>
            </div>
          )}

          {updatedAt && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Updated</p>
              <p className="text-sm">{formatDateIST(updatedAt)}</p>
            </div>
          )}

          {completedAt && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Completed</p>
              <p className="text-sm">{formattedCompletedAt}</p>
            </div>
          )}
        </div>
        {/* Customer Information - Show only fields that exist */}
        {(customerName || customerEmail || customerPhone) && (
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            {customerName && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Customer Name</p>
                <p className="text-sm truncate">{customerName}</p>
              </div>
            )}
            {customerEmail && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Customer Email</p>
                <p className="text-sm break-all">{customerEmail}</p>
              </div>
            )}
            {customerPhone && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Customer Phone</p>
                <p className="text-sm">{customerPhone}</p>
              </div>
            )}
          </div>
        )}

        {/* Note field - Show if available */}
        {note && (
          <div className="mt-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Note</p>
              <p className="text-sm bg-muted/50 p-3 rounded border">{note}</p>
            </div>
          </div>
        )}

        {/* Webhook Status */}
        {webhookDelivered !== undefined && (
          <div className="mt-4 p-3 bg-muted/30 rounded-lg border">
            <div className="flex items-center gap-2">
              <p className="text-xs font-medium text-muted-foreground">Webhook Status:</p>
              {webhookDelivered ? (
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  <Badge variant="default" className="bg-success/10 text-success border-success/20">
                    Delivered
                  </Badge>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <XCircle className="h-4 w-4 text-muted-foreground" />
                  <Badge variant="secondary" className="bg-muted text-muted-foreground">
                    Not Delivered
                  </Badge>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-6">
          {checkoutUrl && (
            <Button variant="outline" onClick={() => window.open(checkoutUrl, "_blank")}>Open Checkout</Button>
          )}
          <Button
            variant="outline"
            onClick={handleCopyReference}
            className="flex items-center gap-2"
          >
            {copiedField === "reference" ? (
              <>
                <Check className="h-4 w-4 text-success" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy Reference
              </>
            )}
          </Button>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
